import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PixelCanvas } from '../components/PixelCanvas';
import { useCanvasWebSocket } from '../hooks/useCanvasWebSocket';
import { ZoomIn, ZoomOut, Hand, Plus, Settings, LogIn, LogOut, X, Check, ChevronLeft, Menu, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Recipe } from '../types';

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canvas, isConnected } = useCanvasWebSocket();
  const [zoom, setZoom] = useState(6);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auth & Recipes
  const [token, setToken] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [processing, setProcessing] = useState<string | null>(null);
  const [showRecipes, setShowRecipes] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(3);
  const [clearing, setClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Hover coordinates
  const [hoverCoords, setHoverCoords] = useState<{ x: number; y: number } | null>(null);

  // Check for auth token
  useEffect(() => {
    const urlToken = searchParams.get('token');
    const urlError = searchParams.get('error');
    const storedToken = localStorage.getItem('auth_token');

    if (urlError === 'unauthorized') {
      toast.error('Zugriff verweigert', {
        description: 'Du bist nicht berechtigt, auf diese Anwendung zuzugreifen.'
      });
      navigate('/', { replace: true });
    } else if (urlToken) {
      localStorage.setItem('auth_token', urlToken);
      setToken(urlToken);
      setShowRecipes(true);
      navigate('/', { replace: true });
    } else if (storedToken) {
      setToken(storedToken);
    }
  }, [searchParams, navigate]);

  // Fetch recipes when authenticated AND sidebar is open
  useEffect(() => {
    if (!token || !showRecipes) {
      // Clear recipes when sidebar is closed to free memory
      if (!showRecipes) {
        setRecipes([]);
      }
      return;
    }

    const fetchRecipes = async () => {
      try {
        const response = await fetch(`/api/recipes/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('auth_token');
            setToken(null);
            return;
          }
          throw new Error('Failed to fetch recipes');
        }

        const data = (await response.json()) as Recipe[];
        setRecipes(data);
      } catch (err) {
        console.error('Error fetching recipes:', err);
      }
    };

    fetchRecipes();
    const interval = setInterval(fetchRecipes, 10000);
    return () => clearInterval(interval);
  }, [token, showRecipes]);

  // Center canvas on load
  useEffect(() => {
    if (containerRef.current && canvas) {
      const container = containerRef.current;
      const canvasSize = 100 * zoom;
      setOffset({
        x: (container.clientWidth - canvasSize) / 2,
        y: (container.clientHeight - canvasSize) / 2
      });
    }
  }, [canvas, zoom]);

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 1 : -1;
      setZoom(prev => Math.max(2, Math.min(12, prev + delta)));
    }
  };

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setLastTouchDistance(getTouchDistance(e.touches));
    } else if (e.touches.length === 1) {
      setIsPanning(true);
      setPanStart({
        x: e.touches[0].clientX - offset.x,
        y: e.touches[0].clientY - offset.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance) {
      e.preventDefault();
      const newDistance = getTouchDistance(e.touches);
      if (newDistance) {
        const scale = newDistance / lastTouchDistance;
        setZoom(prev => Math.max(2, Math.min(12, prev * scale)));
        setLastTouchDistance(newDistance);
      }
    } else if (e.touches.length === 1 && isPanning) {
      setOffset({
        x: e.touches[0].clientX - panStart.x,
        y: e.touches[0].clientY - panStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
    setLastTouchDistance(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const resetView = () => {
    if (containerRef.current && canvas) {
      const container = containerRef.current;
      const canvasSize = 100 * zoom;
      setOffset({
        x: (container.clientWidth - canvasSize) / 2,
        y: (container.clientHeight - canvasSize) / 2
      });
    }
  };

  const handleLogin = () => {
    window.location.href = `/api/auth/twitch`;
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setRecipes([]);
    setShowRecipes(false);
    setShowMobileMenu(false);
  };

  const handleAccept = async (recipeId: string) => {
    if (!token) return;

    setProcessing(recipeId);
    try {
      const response = await fetch(`/api/recipes/${recipeId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to accept recipe');
      }

      setRecipes(recipes.filter(r => r.id !== recipeId));
      toast.success('Rezept akzeptiert!', {
        description: 'Canvas wurde für alle Zuschauer aktualisiert.'
      });
    } catch (err) {
      console.error('Error accepting recipe:', err);
      toast.error('Fehler beim Akzeptieren', {
        description: 'Bitte versuche es erneut.'
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (recipeId: string) => {
    if (!token) return;

    setProcessing(recipeId);
    try {
      const response = await fetch(`/api/recipes/${recipeId}/decline`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to decline recipe');
      }

      setRecipes(recipes.filter(r => r.id !== recipeId));
      toast.success('Rezept abgelehnt', {
        description: 'Das Rezept wurde aus der Warteschlange entfernt.'
      });
    } catch (err) {
      console.error('Error declining recipe:', err);
      toast.error('Fehler beim Ablehnen', {
        description: 'Bitte versuche es erneut.'
      });
    } finally {
      setProcessing(null);
    }
  };

  const handlePreviewWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 1 : -1;
      setPreviewZoom(prev => Math.max(2, Math.min(8, prev + delta)));
    }
  };

  const handlePixelHover = (x: number | null, y: number | null) => {
    if (x !== null && y !== null) {
      setHoverCoords({ x, y });
    } else {
      setHoverCoords(null);
    }
  };

  const handleClearCanvas = async () => {
    if (!token) return;

    setClearing(true);
    try {
      const response = await fetch(`/api/canvas/clear`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to clear canvas');
      }

      toast.success('Canvas geleert!', {
        description: 'Das Canvas wurde für alle Zuschauer zurückgesetzt.'
      });
      setShowClearConfirm(false);
    } catch (err) {
      console.error('Error clearing canvas:', err);
      toast.error('Fehler beim Leeren', {
        description: 'Bitte versuche es erneut.'
      });
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-neutral-900">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-10 border-b border-border bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80">
        <div className="flex items-center justify-between px-3 md:px-4 h-14">
          <div className="flex items-center gap-2 md:gap-4">
            <h1 className="text-base md:text-lg font-semibold">Stegi Canvas</h1>
            <div className="hidden sm:flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm text-muted-foreground">
                {isConnected ? 'Live' : 'Getrennt'}
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/create"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Rezept erstellen
            </Link>

            {token ? (
              <>
                <button
                  onClick={() => setShowRecipes(!showRecipes)}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Rezepte {recipes.length > 0 && `(${recipes.length})`}
                </button>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
              </>
            ) : (
              <button
                onClick={handleLogin}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <LogIn className="w-4 h-4" />
                Mit Twitch anmelden
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 hover:bg-accent transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-border bg-neutral-800 p-3 space-y-2">
            <Link
              to="/create"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Rezept erstellen
            </Link>

            {token ? (
              <>
                <button
                  onClick={() => {
                    setShowRecipes(!showRecipes);
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Rezepte {recipes.length > 0 && `(${recipes.length})`}
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
              </>
            ) : (
              <button
                onClick={handleLogin}
                className="w-full flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <LogIn className="w-4 h-4" />
                Mit Twitch anmelden
              </button>
            )}
          </div>
        )}
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className={`absolute inset-0 pt-14 ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        >
          <PixelCanvas
            canvas={canvas}
            pixelSize={zoom}
            onPixelHover={handlePixelHover}
          />
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-2 md:px-3 py-2 shadow-lg">
          <button
            onClick={() => setZoom(prev => Math.max(2, prev - 1))}
            disabled={zoom <= 2}
            className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Rauszoomen"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          <span className="text-sm text-muted-foreground min-w-[50px] md:min-w-[60px] text-center" title="Zum Zoomen: Strg/Cmd + Scrollen oder Pinch">
            {zoom}x
          </span>

          <button
            onClick={() => setZoom(prev => Math.min(12, prev + 1))}
            disabled={zoom >= 12}
            className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Reinzoomen"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-border mx-1" />

          <button
            onClick={resetView}
            className="inline-flex items-center justify-center rounded-md px-2 md:px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
            title="Ansicht zurücksetzen"
          >
            <Hand className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Zurücksetzen</span>
          </button>

          {token && (
            <>
              <div className="w-px h-6 bg-border mx-1" />

              <button
                onClick={() => setShowClearConfirm(true)}
                className="inline-flex items-center justify-center rounded-md px-2 md:px-3 py-2 text-sm font-medium hover:bg-red-600 hover:text-white transition-colors"
                title="Canvas leeren"
              >
                <Trash2 className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Leeren</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Coordinates Display */}
      {hoverCoords && (
        <div className="absolute top-20 left-4 z-10 bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-3 py-2 rounded-md border border-border shadow-lg">
          <div className="text-sm font-mono space-y-1">
            <div>
              <span className="text-muted-foreground">Canvas:</span>{' '}
              <span className="font-semibold">X: {hoverCoords.x}</span>
              <span className="text-muted-foreground mx-2">Y: {hoverCoords.y}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Minecraft:</span>{' '}
              <span className="font-semibold">X: {-1392 + hoverCoords.x}</span>
              <span className="text-muted-foreground mx-2">Z: {-1380 + hoverCoords.y}</span>
            </div>
          </div>
        </div>
      )}

      {/* Help Text - Hidden on small mobile */}
      <div className="hidden sm:block absolute bottom-4 right-4 z-10 text-xs text-muted-foreground bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-3 py-2 rounded-md border border-border">
        <div>Drag to pan • Ctrl/Cmd + Scroll to zoom</div>
      </div>

      {/* Recipes Sidebar */}
      {token && showRecipes && (
        <div className="absolute top-14 right-0 bottom-0 w-full md:w-96 bg-neutral-800 border-l border-border shadow-2xl transition-transform duration-300 z-20 animate-in slide-in-from-right">
          <div className="flex flex-col h-full">
            {/* Panel Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold">Ausstehende Rezepte</h2>
                <p className="text-xs text-muted-foreground">
                  {recipes.length} {recipes.length === 1 ? 'Rezept wartet' : 'Rezepte warten'}
                </p>
              </div>
              <button
                onClick={() => setShowRecipes(false)}
                className="inline-flex items-center justify-center rounded-md p-2 hover:bg-accent transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
              <span className="text-xs text-muted-foreground">Vorschau-Zoom:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewZoom(prev => Math.max(2, prev - 1))}
                  disabled={previewZoom <= 2}
                  className="inline-flex items-center justify-center rounded-md p-1 text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ZoomOut className="w-3 h-3" />
                </button>
                <span className="text-xs text-muted-foreground min-w-[40px] text-center">
                  {previewZoom}x
                </span>
                <button
                  onClick={() => setPreviewZoom(prev => Math.min(8, prev + 1))}
                  disabled={previewZoom >= 8}
                  className="inline-flex items-center justify-center rounded-md p-1 text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ZoomIn className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Recipes List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {recipes.length === 0 ? (
                <div className="text-center py-12">
                  <Settings className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Keine ausstehenden Rezepte</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Neue Rezepte erscheinen hier, wenn Nutzer sie erstellen
                  </p>
                </div>
              ) : (
                recipes.map((recipe) => {
                  const cost = Math.ceil(recipe.pixels.length / 3);

                  return (
                    <div
                      key={recipe.id}
                      className="border border-border rounded-lg p-4 space-y-3 bg-neutral-800/50"
                    >
                      {/* Recipe Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-sm">
                            Rezept #{recipe.id.slice(0, 8)}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {new Date(recipe.createdAt).toLocaleString('de-DE')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{recipe.pixels.length}</p>
                          <p className="text-xs text-muted-foreground">Pixel</p>
                          <p className="text-xs text-muted-foreground">≈ {cost}€</p>
                        </div>
                      </div>

                      {/* Preview */}
                      <div className="border border-border rounded overflow-hidden bg-neutral-900">
                        <div
                          className="overflow-auto max-h-48"
                          onWheel={handlePreviewWheel}
                        >
                          <PixelCanvas
                            canvas={canvas}
                            highlightPixels={recipe.pixels}
                            pixelSize={previewZoom}
                            canvasOpacity={0.4}
                          />
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleAccept(recipe.id)}
                          disabled={processing === recipe.id}
                          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {processing === recipe.id ? (
                            'Verarbeite...'
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              Akzeptieren
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDecline(recipe.id)}
                          disabled={processing === recipe.id}
                          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {processing === recipe.id ? (
                            'Verarbeite...'
                          ) : (
                            <>
                              <X className="w-4 h-4" />
                              Ablehnen
                            </>
                          )}
                        </button>
                      </div>

                      {/* Recipe Link */}
                      <Link
                        to={`/recipe/${recipe.id}`}
                        className="block text-center text-xs text-primary hover:underline"
                      >
                        Vollständiges Rezept anzeigen →
                      </Link>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recipes Panel Toggle (when collapsed) */}
      {token && !showRecipes && recipes.length > 0 && (
        <button
          onClick={() => setShowRecipes(true)}
          className="absolute top-20 right-4 z-10 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
        >
          <ChevronLeft className="w-4 h-4" />
          {recipes.length} Ausstehend
        </button>
      )}

      {/* Clear Canvas Confirmation Dialog */}
      {showClearConfirm && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-neutral-800 border border-border rounded-lg p-6 max-w-md mx-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Canvas leeren?</h3>
                <p className="text-sm text-muted-foreground">
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Möchtest du wirklich das gesamte Canvas zurücksetzen? Alle Pixel werden auf Weiß gesetzt und die Änderung wird sofort an alle Zuschauer übertragen.
            </p>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearing}
                className="flex-1 inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleClearCanvas}
                disabled={clearing}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {clearing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Leere...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Canvas leeren
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
